/* oxlint-disable react/jsx-props-no-spreading -- props are intentionally forwarded by this adapter */
/* oxlint-disable react/refs -- imperative refs are required for DOM, focus, drag, and media behavior */
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useHover,
  safePolygon,
  useDismiss,
  useInteractions,
  useMergeRefs,
  FloatingPortal,
} from '@floating-ui/react'
import type { Placement } from '@floating-ui/react'
import classNames from 'classnames'
import { useState, useMemo, useContext, createContext, isValidElement, cloneElement, type Ref } from 'react'

interface TooltipOptions {
  initialOpen?: boolean
  placement?: Placement
  open?: boolean
  onMouseOver?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void
  className?: string
  tooltipClassName?: string
  keepOpen?: boolean
}

function useTooltip({
  initialOpen = false,
  placement = `right`,
  open: controlledOpen,
  keepOpen = false,
}: TooltipOptions = {}) {
  const [open, setOpen] = useState(initialOpen)

  const data = useFloating({
    placement,
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(5),
      flip({
        crossAxis: placement.includes(`-`),
        fallbackAxisSideDirection: `start`,
        padding: 5,
      }),
      shift({ padding: 5 }),
    ],
  })

  const context = data.context

  const hover = useHover(context, {
    move: false,
    enabled: controlledOpen == null,
    ...(keepOpen ? { handleClose: safePolygon() } : {}),
  })

  const dismiss = useDismiss(context, { ancestorScroll: true })

  const interactions = useInteractions([hover, dismiss])

  return useMemo(
    () => ({
      open,
      setOpen,
      ...interactions,
      ...data,
    }),
    [open, setOpen, interactions, data],
  )
}

type ContextType = ReturnType<typeof useTooltip> | null

const TooltipContext = createContext<ContextType>(null)

const useTooltipContext = () => {
  const context = useContext(TooltipContext)

  if (context == null) {
    throw new Error(`Tooltip components must be wrapped in <Tooltip />`)
  }

  return context
}

export function Tooltip({
  children,
  message,
  keepOpen = false,
  ...options
}: { children: React.ReactNode; message?: React.ReactNode; keepOpen?: boolean } & TooltipOptions) {
  const tooltip = useTooltip({ ...options, keepOpen })
  return (
    <TooltipContext.Provider value={tooltip}>
      <TooltipTrigger asChild={true} onMouseOver={options.onMouseOver}>
        <span x-component-name="Tooltip" className={classNames(`cursor-default`, options.className)}>
          {children}
        </span>
      </TooltipTrigger>
      <TooltipContent className={classNames(`tooltip-wrapper z-50`, options.tooltipClassName)}>
        {message}
      </TooltipContent>
    </TooltipContext.Provider>
  )
}
// This is the trigger element that will be used to open the tooltip. All props will be forwarded to this element. (See cloneElement inside first return statement and span inside second return statement).
// If `asChild` is true, the user can pass any element as the anchor, otherwise a span will be used.
function TooltipTrigger({
  children,
  asChild = false,
  ref: propRef,
  ...props
}: React.HTMLProps<HTMLElement> & { asChild?: boolean; ref?: Ref<HTMLElement> }) {
  const context = useTooltipContext()
  const childrenRef = isValidElement<React.HTMLProps<HTMLElement> & { ref?: React.Ref<HTMLElement> }>(children)
    ? children.props.ref
    : undefined
  const ref = useMergeRefs([context.refs.setReference, propRef, childrenRef])

  // `asChild` allows the user to pass any element as the anchor
  if (asChild && isValidElement<React.HTMLProps<HTMLElement>>(children)) {
    return cloneElement(
      children,
      context.getReferenceProps({
        ref,
        ...props,
        ...children.props,
        style: { ...children.props.style, pointerEvents: `auto` },
        ...{ 'data-state': context.open ? `open` : `closed` },
      }),
    )
  }

  return (
    <span
      ref={ref}
      className="pointer-events-auto"
      // The user can style the trigger based on the state
      data-state={context.open ? `open` : `closed`}
      {...context.getReferenceProps(props)}
    >
      {children}
    </span>
  )
}
// This is the content that will be shown when the tooltip is open. All props will be forwarded to this element. (See span inside return statement)
function TooltipContent({
  style,
  children,
  ref: propRef,
  ...props
}: React.HTMLProps<HTMLDivElement> & { ref?: Ref<HTMLDivElement> }) {
  const context = useTooltipContext()
  const ref = useMergeRefs([context.refs.setFloating, propRef])

  if (!context.open || !children) return null

  return (
    <FloatingPortal>
      <span
        ref={ref}
        style={{
          ...context.floatingStyles,
          ...style,
        }}
        {...context.getFloatingProps(props)}
      >
        {children}
      </span>
    </FloatingPortal>
  )
}
